import { startOfDay } from "date-fns";
import { EventStatus, InternalEventStatus } from "@/lib/shared/enums";
import { getEventDateRange, type EventDateOption } from "@/lib/utils/event-dates";

/**
 * The minimal event shape the lifecycle predicates read. Both the events-page card
 * (`OrganizationEventCard`) and the switcher's hydrated RPC row satisfy it, so the
 * "active / past / cancelled" definition lives in exactly one place and can't drift
 * between the two surfaces.
 */
export interface EventLifecycleFields {
  /** User-facing `event_status` (e.g. `"cancelled"`). */
  eventStatus?: string | null;
  /** Ops-facing `internal_status` (e.g. `"canceled"`) — set by internal tooling that bypasses the client cancel flow. */
  internalStatus?: string | null;
  /** Whether the event's books are closed. */
  booksClosed: boolean;
  /** The event's `event_dates` (jsonb) — used to resolve the end date for the past check. */
  eventDates?: EventDateOption[] | null;
}

/**
 * True when the event is cancelled by either signal: the user-facing `event_status`
 * (set by the client dashboard's cancel/reopen flow) or the ops `internal_status`
 * (set by internal tooling, e.g. Fetch v2 or the Sourcing Tracker, which bypasses
 * the dashboard flow). Internal cancellations must be reflected here so they're
 * excluded from Active/Past the same way a dashboard cancellation is.
 *
 * @param event - The event's lifecycle fields.
 * @returns Whether the event is cancelled.
 */
export function isEventCancelled(event: EventLifecycleFields): boolean {
  return event.eventStatus === EventStatus.CANCELLED || event.internalStatus === InternalEventStatus.CANCELED;
}

/**
 * True when a non-cancelled event belongs in the Past bucket: its books are closed, OR its
 * end date has already elapsed (strictly before today, local midnight). Undated events are
 * never Past — we can't call an event past without a date. Cancelled events are never Past
 * (they have their own bucket).
 *
 * @param event - The event's lifecycle fields.
 * @param reference - "Now" for the past cutoff; defaults to the current time (injectable for tests).
 * @returns Whether the event is Past.
 */
export function isEventPast(event: EventLifecycleFields, reference: Date = new Date()): boolean {
  if (isEventCancelled(event)) return false;
  if (event.booksClosed) return true;

  const range = getEventDateRange({ event_dates: event.eventDates ?? [] });
  if (!range) return false;
  return range.endDate.getTime() < startOfDay(reference).getTime();
}

/**
 * True when an event is Active: not cancelled and not past (books open and its end date is
 * today or later, or it has no date at all).
 *
 * @param event - The event's lifecycle fields.
 * @param reference - "Now" for the past cutoff; defaults to the current time (injectable for tests).
 * @returns Whether the event is Active.
 */
export function isEventActive(event: EventLifecycleFields, reference: Date = new Date()): boolean {
  return !isEventCancelled(event) && !isEventPast(event, reference);
}

/** Which lifecycle bucket an event falls into — the switcher groups + tags by this. */
export type EventBucket = "active" | "past" | "cancelled";

/**
 * Canonical order for buckets — Active first, then Past, then Cancelled. Single source for both
 * the switcher's server-side sort and its client-side section rendering so the two can't disagree.
 */
export const EVENT_BUCKET_ORDER: readonly EventBucket[] = ["active", "past", "cancelled"];

/**
 * Classify an event into exactly one lifecycle bucket, using the same predicates as the
 * events-page tabs so the switcher and the page can never disagree. Cancelled wins over past.
 *
 * @param event - The event's lifecycle fields.
 * @param reference - "Now" for the past cutoff; defaults to the current time (injectable for tests).
 * @returns `"cancelled"`, `"past"`, or `"active"`.
 */
export function classifyEvent(event: EventLifecycleFields, reference: Date = new Date()): EventBucket {
  if (isEventCancelled(event)) return "cancelled";
  if (isEventPast(event, reference)) return "past";
  return "active";
}
